import React, { useEffect, useRef, useState } from "react";
import { Eye, Box, ArrowUp, Battery, Target, Sparkles } from "lucide-react";
import homeSections from "@/content/home-sections.json";

const APP_ICONS = { eye: Eye, box: Box, arrow: ArrowUp, battery: Battery, target: Target, sparkles: Sparkles };
const APPS = homeSections.applications.items.map((item) => ({ ...item, Icon: APP_ICONS[item.icon] || Sparkles }));

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const els = el.querySelectorAll(".reveal");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); });
    }, { threshold: 0.12 });
    els.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);
  return ref;
};

const AppCard = ({ a }) => {
  const Icon = a.Icon;
  return (
    <div data-testid={`app-${a.id}`} className="reveal geniq-card geniq-card-spotlight p-6 cursor-default group">
      <div className="flex items-start mb-4">
        <div className="w-11 h-11 rounded-full border border-[#764CB0]/50 flex items-center justify-center bg-black/40 group-hover:border-[#B79BE0] transition-colors">
          <Icon size={18} strokeWidth={1.4} className="text-[#B79BE0]" />
        </div>
      </div>
      <h3 className="text-white text-[16px] font-medium mb-3 leading-snug">{a.title}</h3>
      <p className="text-body text-[13px] leading-[1.6]">{a.text}</p>
    </div>
  );
};

export const Section4 = () => {
  const ref = useReveal();
  return (
    <section ref={ref} data-testid="section-applications" className="relative py-28 overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
      <div className="container-geniq relative z-10">
        <div className="mb-16">
          <div className="reveal text-[11px] uppercase tracking-[0.22em] text-white/45 mb-4">{homeSections.applications.eyebrow}</div>
          <h2 className="reveal text-[32px] sm:text-[42px] lg:text-[48px] font-normal leading-[1.1] text-white mb-5">
            {homeSections.applications.title} <span className="gradient-text">{homeSections.applications.accent_title}</span>
          </h2>
        </div>

        {/* Layout: 3 cards | core | 3 cards */}
        <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-12 items-center">
          {/* Left column (cards 0-2) */}
          <div className="flex flex-col gap-5 relative">
            {APPS.slice(0, 3).map((a, i) => (
              <div key={a.id} className="relative">
                <AppCard a={a} />
                {/* connector to core - desktop only */}
                <div className="hidden lg:block absolute top-1/2 -right-12 w-12 h-px bg-gradient-to-r from-[#764CB0]/30 to-transparent" />
              </div>
            ))}
          </div>

          {/* Core */}
          <div className="hidden lg:flex items-center justify-center relative">
            <div className="orbit-ring spin-slow absolute" style={{ width: 220, height: 220 }} />
            <div className="orbit-ring spin-reverse absolute" style={{ width: 320, height: 320, opacity: 0.5 }} />
            <div className="absolute -inset-16 rounded-full" style={{ background: "radial-gradient(circle, rgba(118,76,176,0.25), transparent 60%)" }} />
            <div className="relative w-32 h-32 rounded-full border border-[#B79BE0]/60 flex flex-col items-center justify-center bg-black/60 backdrop-blur-md">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/55">core</span>
              <span className="text-white text-[20px] font-semibold mt-1">GENIQ</span>
              <span className="absolute inset-0 rounded-full pulse-dot" style={{ boxShadow: "0 0 30px rgba(118,76,176,0.55)" }} />
            </div>
          </div>

          {/* Right column (cards 3-5) */}
          <div className="flex flex-col gap-5 relative">
            {APPS.slice(3, 6).map((a, i) => (
              <div key={a.id} className="relative">
                <AppCard a={a} />
                <div className="hidden lg:block absolute top-1/2 -left-12 w-12 h-px bg-gradient-to-l from-[#764CB0]/30 to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Section4;
